import { Injectable, inject } from '@angular/core';
import { 
  CanActivate, 
  ActivatedRouteSnapshot, 
  RouterStateSnapshot, 
  UrlTree, 
  Router 
} from '@angular/router';
import { Observable } from 'rxjs';
import { RbacService } from '../services/rbac/permission.service';
import { IT_ASSET_MODULE_ID } from '../shared/constants/it-asset-actions';
import { SettingsAccessService } from '../components/client-super-admin/settings-access.service';

// Define a map that links URL segments to the required Module ID (same terminology as CRM).
// Note: We don't assign a moduleId to 'settings' as it uses a special 'unauthorized' check.
// Budget: backend sends budget actions under IT Asset (module 4), so budget route uses module 4 + budget actions check.
const MODULE_ROUTE_MAP: { urlSegment: string; moduleId?: number; }[] = [
  { urlSegment: 'settings' },
  { urlSegment: 'crm', moduleId: 1 },
  { urlSegment: 'finance', moduleId: 2 },
  { urlSegment: 'assets', moduleId: 4 }, // IT Asset module (backend moduleId 4)
  { urlSegment: 'budget', moduleId: 4 }, // Budget: IT Asset module + hasAnyBudgetTabAccess() in guard
];

@Injectable({
  providedIn: 'root'
})
export class ModuleGuard implements CanActivate {
  isOrgAdmin=false
  
  private router = inject(Router);
  private rbac = inject(RbacService);
  private settingsAccess = inject(SettingsAccessService);
  private defaultRedirectPath = ['/dashboard/home']; // Path for denied access

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {

     let permision = JSON.parse(localStorage.getItem('permissions') || '') || {};

    if ('issystemRole' in permision) {
      permision.issystemRole ? this.isOrgAdmin = true : this.isOrgAdmin = false;
    } else {
      this.isOrgAdmin = false;
    }

    if (this.isOrgAdmin || this.rbac.isAdmin()) {
      return true;
    }
    
    const fullUrl = state.url.toLowerCase(); 
    console.log(`Guard Full URL: ${fullUrl}`); 

    // 1. Get and Parse Permissions data
    let permissions: any;
    try {
      const permissionsString = localStorage.getItem('permissions') || '{}';
      permissions = JSON.parse(permissionsString);
    } catch (e) {
      console.error('Error parsing permissions from localStorage:', e);
      permissions = {};
    }
    const accessData: any[] = permissions?.modules || [];

    // 2. Determine the required Module ID/Check based on the URL
    const targetModule = MODULE_ROUTE_MAP.find(mapping => 
      fullUrl.includes(mapping.urlSegment)
    );
    
    // 3. Core Access Logic
    if (targetModule) {
      
      // 3A. 🔑 SETTINGS ROUTE: module 12 + granted Settings tabs (org/system admins bypass)
      if (targetModule.urlSegment === 'settings') {
        this.settingsAccess.refresh();
        if (this.settingsAccess.canAccessSettings()) {
          console.log('Settings access granted via module 12 permissions / admin.');
          return true;
        }
        console.log('Settings access denied. No Settings module access.');
        return this.router.createUrlTree(this.defaultRedirectPath);
      }

      // 3B. 🔑 BUDGET ROUTE: backend sends budget under IT Asset (module 4) with action ids 147–153
      if (targetModule.urlSegment === 'budget') {
        const hasModuleAccess = accessData.some(
          (item: any) => (item.id ?? item.moduleId) === IT_ASSET_MODULE_ID
        );
        const hasBudgetTabAccess = this.rbac.hasAnyBudgetTabAccess();
        const allowed = hasModuleAccess && hasBudgetTabAccess;
        console.log(`Budget route: module ${IT_ASSET_MODULE_ID} access=${hasModuleAccess}, budget tab access=${hasBudgetTabAccess}, allowed=${allowed}`);
        if (allowed) return true;
        console.log('Budget access denied. Redirecting.');
        return this.router.createUrlTree(this.defaultRedirectPath);
      }

      // 3B2. 🔑 FINANCE ROUTE: module 2 + view action (`get-finace-records` / id 65)
      if (targetModule.urlSegment === 'finance') {
        const allowed = this.rbac.hasFinanceModuleAccess();
        console.log(`Finance route: view access=${allowed}`);
        if (allowed) return true;
        console.log('Finance access denied. Redirecting.');
        return this.router.createUrlTree(this.defaultRedirectPath);
      }

      // 3C. 🔑 GRANULAR MODULE CHECK (for 'crm', 'assets', etc.)
      const requiredModuleId = targetModule.moduleId;
      
      // Safety check: ensure a moduleId was actually defined for this segment
      if (requiredModuleId === undefined) {
         console.error(`Missing moduleId for segment: ${targetModule.urlSegment}. Denying access.`);
         return this.router.createUrlTree(this.defaultRedirectPath);
      }

      // Finance: same resolution as sidebar (`hasFinanceModuleAccess` — id + name + hasAccess).
      if (targetModule.urlSegment === 'finance') {
        const allowed = this.rbac.hasFinanceModuleAccess();
        console.log(`Finance route: hasFinanceModuleAccess=${allowed}`);
        if (allowed) return true;
        console.log('Finance access denied. Redirecting.');
        return this.router.createUrlTree(this.defaultRedirectPath);
      }
      
      // Require module present with hasAccess === true (support backend id or moduleId)
      const matchedModule = accessData.find(
        (item: any) => (item.id ?? item.moduleId) === requiredModuleId,
      );
      const hasModuleAccess = matchedModule?.hasAccess === true;
      
      console.log(`Checking access for Module ID ${requiredModuleId}. Has Access: ${hasModuleAccess}`);

      if (hasModuleAccess) {
        // Access granted for this protected route
        return true;
      } else {
        // Access denied, redirect to the default path
        console.log('Module access denied. Redirecting.');
        return this.router.createUrlTree(this.defaultRedirectPath);
      }
    }

     return this.router.createUrlTree(this.defaultRedirectPath);

    // 4. Default: No protected module segment was found in the URL.
    // return false; 
  }
}